import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import {
  View, Text, ScrollView, StyleSheet, TextInput,
  TouchableOpacity, ActivityIndicator, Platform,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '../../../../theme/useTheme';
import { useSelector } from 'react-redux';
import AdminBreadcrumb from '../../components/AdminBreadcrumb';
import AdminButton from '../../components/AdminButton';
import AdminTag, { TagVariant } from '../../components/AdminTag';
import AdminStatusBadge, { getStatusType } from '../../components/AdminStatusBadge';
import { AdminColors } from '../../constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import { Location } from './locationTypes';
import { getUserDisplayName } from './LocationUserSearchSelect';
import LHDataTable from '../../../../components/LHDataTable';
import { useToast } from '../../../../components/LhToastContainer';
import type { RootState } from '../../../../store';
import { createApiClient, getLocalTimestamp, getTimezoneOffsetString } from '../../../../api/apiClient';
import { Route, RouteListResponse, ROUTE_TYPE_OPTIONS } from '../RouteManagement/routeManagementTypes';
import { updateRouteType, fetchActiveRouteTypes } from '../RouteManagement/routeManagementApi';
import RouteDataTable from '../RouteManagement/RouteDataTable';
import AddRouteModal from './AddRouteModal';
import BulkAddRoutesModal from './BulkAddRoutesModal';

// ─── Inline select for Route Type column ──────────────────────────────────────
const InlineSelect: React.FC<{
  value: string | null;
  options: { label: string; value: string }[];
  onChange: (val: string) => void;
  disabled?: boolean;
}> = ({ value, options, onChange, disabled }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const displayValue = value ?? '';

  if (Platform.OS === 'web') {
    const Tag = 'select' as any;
    const Opt = 'option' as any;
    const webStyle = {
      ...inlineSelectWebStyle,
      ...(isDark
        ? {
            backgroundColor: colors.input.background,
            borderColor: colors.input.border,
            color: colors.text.primary,
          }
        : {}),
    };
    return (
      <Tag
        value={displayValue}
        onChange={(e: any) => onChange(e.target.value)}
        disabled={disabled}
        style={webStyle}
      >
        <Opt value="">-- Select --</Opt>
        {options.map((o: any) => <Opt key={o.value} value={o.value}>{o.label}</Opt>)}
      </Tag>
    );
  }
  const idx = options.findIndex(o => o.value === displayValue);
  const label = idx >= 0 ? options[idx].label : '-- Select --';
  return (
    <TouchableOpacity
      style={[
        routeStyles.nativeSelect,
        isDark && {
          backgroundColor: colors.input.background,
          borderColor: colors.input.border,
        },
      ]}
      onPress={() => { if (disabled) return; const next = options[(idx + 1) % options.length]; onChange(next.value); }}
      activeOpacity={0.7}
      disabled={disabled}
    >
      <Text style={[routeStyles.nativeSelectText, isDark && { color: colors.text.primary }]}>{label}</Text>
      <Text style={[routeStyles.nativeArrow, isDark && { color: colors.text.secondary }]}>▾</Text>
    </TouchableOpacity>
  );
};

const inlineSelectWebStyle: Record<string, any> = {
  height: 28, paddingLeft: 8, paddingRight: 8, paddingTop: 2, paddingBottom: 2,
  borderWidth: 1, borderStyle: 'solid', borderColor: AdminColors.borderDefault,
  borderRadius: 4, fontSize: 12, color: AdminColors.textPrimary,
  backgroundColor: AdminColors.bgSurface, cursor: 'pointer',
  appearance: 'auto', fontFamily: FontFamily.regular,
};

// ─── Toggle switch ────────────────────────────────────────────────────────────
const RouteToggle: React.FC<{ isActive: boolean; onPress: () => void }> = ({ isActive, onPress }) => (
  <TouchableOpacity onPress={onPress} activeOpacity={0.8}>
    <View style={[routeStyles.toggleContainer, isActive ? routeStyles.toggleActive : routeStyles.toggleInactive]}>
      <View style={[routeStyles.toggleThumb, isActive ? routeStyles.toggleThumbActive : routeStyles.toggleThumbInactive]} />
    </View>
  </TouchableOpacity>
);

function roleToVariant(role: string): TagVariant {
  if (!role) return 'gray';
  const r = role.toLowerCase();
  if (r.includes('leader'))     return 'blue';
  if (r.includes('supervisor')) return 'teal';
  if (r.includes('certif'))     return 'purple';
  if (r.includes('checker'))    return 'orange';
  if (r.includes('super'))      return 'red';
  if (r.includes('market'))     return 'amber';
  if (r.includes('coe'))        return 'green';
  return 'gray';
}

function formatLastLoggedAt(value?: string): string {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const dd = String(parsed.getDate()).padStart(2, '0');
  const yyyy = parsed.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

export interface LocationDetailProps {
  location: Location;
  employees: any[];
  onBack: () => void;
  onBackToList: () => void;
  onEdit?: () => void;
}

const EMPLOYEE_COLUMNS = [
  {
    columnKey: 'name',
    header: 'Employee Name',
    sortable: true,
    flexGrow: 2,
    renderCell: (item: any) => (
      <Text style={{ fontSize: 14, color: '#3A3A3A', fontFamily: FontFamily.regular }} numberOfLines={2}>
        {item.name || '-'}
      </Text>
    ),
  },
  {
    columnKey: 'gpid',
    header: 'Employee ID',
    sortable: true,
    flexGrow: 1.5,
  },
  {
    columnKey: 'role',
    header: 'Access Level',
    sortable: false,
    flexGrow: 1.5,
    renderCell: (item: any) =>
      item.role
        ? <AdminTag label={item.role} variant={roleToVariant(item.role)} small />
        : <Text style={{ fontSize: 12, color: AdminColors.textMuted, fontFamily: FontFamily.regular }}>-</Text>,
  },
  {
    columnKey: 'lastLoggedAt',
    header: 'Last Login Date',
    sortable: true,
    flexGrow: 1.5,
    formatter: formatLastLoggedAt,
    getSortValue: (item: any) =>
      item.lastLoggedAt ? new Date(item.lastLoggedAt).getTime() : 0,
  },
];

const LocationDetail: React.FC<LocationDetailProps> = ({ location, employees: fieldUsers, onBack, onBackToList, onEdit }) => {
  const toast = useToast();
  const requestedBy = useSelector((state: RootState) => state.appData?.userInfo?.email ?? (state as any).auth?.user?.email ?? 'admin');
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';

  // ── Route Management state ────────────────────────────────────────────────
  const [routes, setRoutes] = useState<Route[]>([]);
  const [isLoadingRoutes, setIsLoadingRoutes] = useState(false);
  const [routeSearch, setRouteSearch] = useState('');
  const [routePage, setRoutePage] = useState(0);
  const [routeTotalPages, setRouteTotalPages] = useState(0);
  const [routeTotalCount, setRouteTotalCount] = useState(0);
  const [routeIsFirst, setRouteIsFirst] = useState(true);
  const [routeIsLast, setRouteIsLast] = useState(false);
  const [duoStatus, setDuoStatus] = useState(location.isDuo ?? false);
  const [addRouteModalVisible, setAddRouteModalVisible] = useState(false);
  const [bulkAddModalVisible, setBulkAddModalVisible] = useState(false);
  const ROUTE_PAGE_SIZE = 10;
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({});
  const [routeTypeOptions, setRouteTypeOptions] = useState<{ label: string; value: string }[]>(ROUTE_TYPE_OPTIONS);
  const [routeLoadParams, setRouteLoadParams] = useState({
    page: 0,
    size: ROUTE_PAGE_SIZE,
    sort: 'routeNumber,asc',
    searchTerm: '',
    routeTypes: undefined as string[] | undefined,
    isActiveList: undefined as boolean[] | undefined,
  });
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load route type options from API once on mount; fall back to static list on error
  useEffect(() => {
    fetchActiveRouteTypes()
      .then(opts => { if (opts.length > 0) setRouteTypeOptions(opts); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!location.name) return;
      setIsLoadingRoutes(true);
      try {
        const api = createApiClient();
        const queryParams: Record<string, any> = {
          locationName: location.name,
          page: routeLoadParams.page,
          size: routeLoadParams.size,
          sort: routeLoadParams.sort,
        };
        if (routeLoadParams.searchTerm) queryParams.searchTerm = routeLoadParams.searchTerm;
        if (routeLoadParams.routeTypes?.length) queryParams.routeTypes = routeLoadParams.routeTypes.join(',');
        if (routeLoadParams.isActiveList?.length) queryParams.isActive = routeLoadParams.isActiveList.join(',');
        const res = await api.get<RouteListResponse>('/perfect-truck/routes/all', { params: queryParams });
        if (!cancelled) {
          const d = res.data;
          setRoutes(d.data);
          setRoutePage(d.currentPage);
          setRouteTotalPages(d.totalPages);
          setRouteTotalCount(d.totalCount);
          setRouteIsFirst(d.isFirst);
          setRouteIsLast(d.isLast);
        }
      } catch {
        if (!cancelled) toast.show('Failed to load routes', 'error');
      } finally {
        if (!cancelled) setIsLoadingRoutes(false);
      }
    })();
    return () => { cancelled = true; };
  }, [location.name, routeLoadParams]);

  const handleRouteTypeChange = useCallback(async (id: number, newType: string) => {
    const prev = routes.find(r => r.id === id);
    if (!prev || prev.routeType === newType) return;
    setRoutes(cur => cur.map(r => r.id === id ? { ...r, routeType: newType } : r));
    try {
      await updateRouteType(id, location.id, newType, requestedBy);
      toast.show('Route type updated', 'success');
    } catch {
      setRoutes(cur => cur.map(r => r.id === id ? { ...r, routeType: prev.routeType } : r));
      toast.show('Failed to update route type', 'error');
    }
  }, [routes, requestedBy, location.id]);

  const handleToggleActive = useCallback(async (id: number) => {
    const route = routes.find(r => r.id === id);
    if (!route) return;
    const newActive = !route.isActive;
    setRoutes(prev => prev.map(r => r.id === id ? { ...r, isActive: newActive } : r));
    try {
      const api = createApiClient();
      await api.patch(`/perfect-truck/locations/${location.id}/routes/${id}/toggle-active`, {
        isActive: newActive,
        requestedBy,
      }, {
        params: {
          clientTimestamp: getLocalTimestamp(),
          clientTimezone: getTimezoneOffsetString(),
        },
      });
      toast.show(newActive ? 'Route activated' : 'Route deactivated', 'success');
    } catch {
      setRoutes(prev => prev.map(r => r.id === id ? { ...r, isActive: route.isActive } : r));
      toast.show('Failed to update route status', 'error');
    }
  }, [routes, requestedBy, location.id]);

  const handleRouteSearchChange = (text: string) => {
    setRouteSearch(text);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setRouteLoadParams(prev => ({ ...prev, searchTerm: text.trim(), page: 0 }));
    }, 350);
  };

  const deriveApiFilters = (filters: Record<string, string[]>) => ({
    routeTypes: (filters['routeType'] ?? []).length > 0 ? filters['routeType'] : undefined,
    isActiveList: (filters['isActive'] ?? []).length > 0
      ? (filters['isActive'].map(v => v === 'true') as boolean[])
      : undefined,
  });

  const handleColumnFilter = (key: string, values: string[]) => {
    const newFilters = { ...columnFilters, [key]: values };
    setColumnFilters(newFilters);
    setRouteLoadParams(prev => ({ ...prev, ...deriveApiFilters(newFilters), page: 0 }));
  };

  const handleRouteSortChange = (key: string, direction: 'asc' | 'desc') => {
    setRouteLoadParams(prev => ({ ...prev, sort: `${key},${direction}`, page: 0 }));
  };

  const handleClearRouteFilters = () => {
    setRouteSearch('');
    setColumnFilters({});
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    setRouteLoadParams(prev => ({
      ...prev,
      searchTerm: '',
      routeTypes: undefined,
      isActiveList: undefined,
      page: 0,
    }));
  };

  const hasActiveRouteFilters = !!routeSearch || Object.values(columnFilters).some(v => v.length > 0);

  const routeColumns = useMemo(() => [
    {
      columnKey: 'routeNumber',
      header: 'Route Number',
      flexGrow: 1.5,
      sortable: true,
    },
    {
      columnKey: 'routeType',
      header: 'Route Type',
      flexGrow: 2,
      sortable: true,
      filterOptions: routeTypeOptions,
      renderCell: (item: Route) => (
        <InlineSelect
          value={item.routeType}
          options={routeTypeOptions}
          onChange={val => handleRouteTypeChange(item.id, val)}
        />
      ),
    },
    {
      columnKey: 'isActive',
      header: 'Active',
      flexGrow: 1,
      sortable: false,
      filterOptions: [
        { label: 'Active', value: 'true' },
        { label: 'Inactive', value: 'false' },
      ],
      renderCell: (item: Route) => (
        <RouteToggle
          isActive={item.isActive}
          onPress={() => handleToggleActive(item.id)}
        />
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [routes, routeTypeOptions]);

  const employees = useMemo(() =>
    fieldUsers
      .map((u: any) => ({
        name:         getUserDisplayName(u),
        gpid:         u.GPID         ?? '',
        email:        u.UserEmail    ?? '',
        role:         u.Role         ?? '',
        lastLoggedAt: u.LastLoggedAt ?? u.lastLoggedAt ?? '',
      }))
      .filter(u => u.name || u.email),
    [fieldUsers]
  );

  return (
    <ScrollView style={[screenStyles.container, isDark && { backgroundColor: colors.background.page }]} contentContainerStyle={screenStyles.content}>
      <AdminBreadcrumb
        items={[
          { label: 'Configuration Module', onPress: onBack },
          { label: 'Location Management', onPress: onBackToList },
          { label: location.name },
        ]}
      />
      <View style={screenStyles.pageHeader}>
        <Text style={[screenStyles.pageTitle, isDark && { color: colors.text.primary }]}>{location.name}</Text>
        <View style={screenStyles.headerActions}>
          <AdminButton label="Back" variant="secondary" onPress={onBackToList} />
          {onEdit && <AdminButton label="Edit" variant="primary" onPress={onEdit} />}
        </View>
      </View>

      <View style={styles.grid}>
        {/* Primary Info */}
        <View style={[styles.section, isDark && { backgroundColor: colors.background.surface }]}>
          <Text style={[styles.sectionTitle, isDark && { color: colors.text.primary, borderBottomColor: colors.border.default }]}>Primary Information</Text>
          <View style={styles.fieldGrid}>
            {[
              { label: 'Location ID', value: location.id },
              { label: 'SAP ID', value: location.sap },
              { label: 'Location Name', value: location.name },
              { label: 'Status', value: location.status, isStatus: true },
            ].map(f => (
              <View key={f.label} style={styles.field}>
                <Text style={[styles.fieldLabel, isDark && { color: colors.text.disabled }]}>{f.label}</Text>
                {f.isStatus
                  ? <AdminStatusBadge status={getStatusType(f.value)} label={f.value} />
                  : <Text style={[styles.fieldValue, isDark && { color: colors.text.primary }]}>{f.value}</Text>
                }
              </View>
            ))}
          </View>
        </View>

        {/* Facility Classification */}
        <View style={[styles.section, isDark && { backgroundColor: colors.background.surface }]}>
          <Text style={[styles.sectionTitle, isDark && { color: colors.text.primary, borderBottomColor: colors.border.default }]}>Facility Classification</Text>
          <View style={styles.fieldGrid}>
            {[
              { label: 'Location Type', value: location.facilityType || '' },
              { label: 'Department', value: location.type },
              { label: 'Organization Type', value: location.org },
              { label: '3PL Status', value: location.tpl },
              { label: 'Location Size', value: location.size },
            ].map(f => (
              <View key={f.label} style={styles.field}>
                <Text style={[styles.fieldLabel, isDark && { color: colors.text.disabled }]}>{f.label}</Text>
                <Text style={[styles.fieldValue, isDark && { color: colors.text.primary }]}>{f.value}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Geographic */}
        <View style={[styles.section, isDark && { backgroundColor: colors.background.surface }]}>
          <Text style={[styles.sectionTitle, isDark && { color: colors.text.primary, borderBottomColor: colors.border.default }]}>Geographic Information</Text>
          <View style={styles.fieldGrid}>
            {[
              { label: 'Region', value: location.region },
              { label: 'Market', value: location.market },
            ].map(f => (
              <View key={f.label} style={styles.field}>
                <Text style={[styles.fieldLabel, isDark && { color: colors.text.disabled }]}>{f.label}</Text>
                <Text style={[styles.fieldValue, isDark && { color: colors.text.primary }]}>{f.value}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Resource Assignment */}
        <View style={[styles.section, isDark && { backgroundColor: colors.background.surface }]}>
          <Text style={[styles.sectionTitle, isDark && { color: colors.text.primary, borderBottomColor: colors.border.default }]}>Resource Assignment</Text>
          <View style={styles.fieldGrid}>
            {[
              { label: 'Capability Resource', value: location.coe || '—' },
              { label: 'E2E Resource', value: location.coe2 || '—' },
              { label: 'Market Lead', value: location.marketLead || '—' },
            ].map(f => (
              <View key={f.label} style={styles.field}>
                <Text style={[styles.fieldLabel, isDark && { color: colors.text.disabled }]}>{f.label}</Text>
                <Text style={[styles.fieldValue, isDark && { color: colors.text.primary }]}>{f.value}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Systems */}
        <View style={[styles.section, isDark && { backgroundColor: colors.background.surface }]}>
          <Text style={[styles.sectionTitle, isDark && { color: colors.text.primary, borderBottomColor: colors.border.default }]}>Systems</Text>
          <View style={styles.fieldGrid}>
            {[
              { label: 'Execution System', value: location.executionSystem || '—' },
            ].map(f => (
              <View key={f.label} style={styles.field}>
                <Text style={[styles.fieldLabel, isDark && { color: colors.text.disabled }]}>{f.label}</Text>
                <Text style={[styles.fieldValue, isDark && { color: colors.text.primary }]}>{f.value}</Text>
              </View>
            ))}
          </View>
        </View>
      </View>

      {/* Employees - Separate from grid */}
      <View style={[styles.employeeSection, isDark && { backgroundColor: colors.background.surface }]}>
        <View style={styles.sectionTitleRow}>
          <Text style={[styles.sectionTitle, isDark && { color: colors.text.primary, borderBottomColor: colors.border.default }]}>Active Employees with Navigator Access</Text>
        </View>
        <Text style={[styles.summaryText, isDark && { color: colors.text.secondary }]}>
          {employees.length} active {employees.length === 1 ? 'employee' : 'employees'} at this location.
        </Text>
        <LHDataTable
          data={employees}
          columns={EMPLOYEE_COLUMNS}
          tableHeader=""
          tableDescription=""
          maxRecordsToShow={25}
          disableRowHighlight
          showHeaderTopBorder={false}
        />
      </View>
      {/* Route Management - below employees */}
      <View style={[styles.employeeSection, isDark && { backgroundColor: colors.background.surface }]}>
        {/* Header row */}
        <View style={[routeStyles.sectionHeaderRow, isDark && { borderBottomColor: colors.border.default }]}>
          <Text style={[routeStyles.sectionHeading, isDark && { color: colors.text.primary }]}>Route Management</Text>
          <Text style={[routeStyles.routeCount, isDark && { color: colors.text.secondary }]}>{routeTotalCount} {routeTotalCount === 1 ? 'route' : 'routes'}</Text>
        </View>

        {/* DUO Status Toggle */}
        <View style={[routeStyles.duoSection, isDark && { borderBottomColor: colors.border.default }]}>
          <View style={routeStyles.duoToggleRow}>
            <Text style={[routeStyles.duoLabel, isDark && { color: colors.text.primary }]}>DUO Status</Text>
            <TouchableOpacity
              onPress={async () => {
                const newVal = !duoStatus;
                setDuoStatus(newVal);
                try {
                  const api = createApiClient();
                  await api.patch(`/admin/locations/${encodeURIComponent(location.id)}/duo-status`, {
                    isDuo: newVal,
                    requestedBy,
                  });
                  toast.show(newVal ? 'DUO Enabled' : 'DUO Disabled', 'success');
                } catch {
                  setDuoStatus(!newVal);
                  toast.show('Failed to update DUO status', 'error');
                }
              }}
              activeOpacity={0.8}
            >
              <View style={[routeStyles.duoToggle, duoStatus && routeStyles.duoToggleActive]}>
                <View style={[routeStyles.duoThumb, duoStatus && routeStyles.duoThumbActive]} />
              </View>
            </TouchableOpacity>
            <Text style={[routeStyles.duoStatusText, isDark && { color: colors.text.secondary }]}>{duoStatus ? 'Enabled' : 'Disabled'}</Text>
          </View>
        </View>

        {/* Search bar + Buttons */}
        <View style={routeStyles.searchAndButtonsRow}>
          <View style={routeStyles.searchLeftSection}>
            <View style={[routeStyles.searchBox, isDark && { backgroundColor: colors.input.background, borderColor: colors.input.border }]}>
              <MaterialIcons name="search" size={16} color={isDark ? colors.text.secondary : AdminColors.textMuted} style={{ marginRight: 6 }} />
              <TextInput
                style={[routeStyles.searchInput, isDark && { color: colors.text.primary }]}
                placeholder="Search routes..."
                placeholderTextColor={isDark ? colors.text.disabled : AdminColors.textMuted}
                value={routeSearch}
                onChangeText={handleRouteSearchChange}
              />
              {!!routeSearch && (
                <TouchableOpacity onPress={() => handleRouteSearchChange('')} activeOpacity={0.7}>
                  <MaterialIcons name="close" size={14} color={isDark ? colors.text.secondary : AdminColors.textMuted} />
                </TouchableOpacity>
              )}
            </View>
            <Text style={[routeStyles.routeCountLabel, isDark && { color: colors.text.secondary }]}>{routeTotalCount} routes</Text>
            {hasActiveRouteFilters && (
              <AdminButton label="Clear Filters" variant="secondary" small onPress={handleClearRouteFilters} />
            )}
          </View>
          <View style={routeStyles.buttonGroup}>
            <TouchableOpacity style={routeStyles.btnSecondary} onPress={() => setBulkAddModalVisible(true)}>
              <Text style={routeStyles.btnSecondaryText}>Bulk Add</Text>
            </TouchableOpacity>
            <TouchableOpacity style={routeStyles.btnPrimary} onPress={() => setAddRouteModalVisible(true)}>
              <Text style={routeStyles.btnPrimaryText}>+ Add Route</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Table */}
        <RouteDataTable
          data={routes}
          columns={routeColumns}
          tableHeader=""
          tableDescription={routeTotalCount > 0 ? `Showing ${routePage * ROUTE_PAGE_SIZE + 1}–${Math.min((routePage + 1) * ROUTE_PAGE_SIZE, routeTotalCount)} of ${routeTotalCount} routes` : ''}
          showHeaderTopBorder={false}
          onRowClick={() => {}}
          isLoading={isLoadingRoutes}
          maxRecordsToShow={ROUTE_PAGE_SIZE}
          externalPage={routePage}
          externalTotalPages={routeTotalPages}
          onExternalPageChange={(newPage: number) => setRouteLoadParams(prev => ({ ...prev, page: newPage }))}
          onPageSizeChange={(newSize: number) => setRouteLoadParams(prev => ({ ...prev, size: newSize, page: 0 }))}
          onSort={handleRouteSortChange}
          isFirstPage={routeIsFirst}
          isLastPage={routeIsLast}
          columnFilters={columnFilters}
          onColumnFilter={handleColumnFilter}
        />
      </View>

      <BulkAddRoutesModal
        visible={bulkAddModalVisible}
        onClose={() => setBulkAddModalVisible(false)}
        locationId={location.id}
        locationName={location.name}
        routeTypeOptions={routeTypeOptions}
        onSuccess={() => setRouteLoadParams(prev => ({ ...prev, page: 0 }))}
      />

      <AddRouteModal
        visible={addRouteModalVisible}
        onClose={() => setAddRouteModalVisible(false)}
        locationId={location.id}
        locationName={location.name}
        onSuccess={() => setRouteLoadParams(prev => ({ ...prev, page: 0 }))}
        routeTypeOptions={routeTypeOptions}
      />
    </ScrollView>
  );
};

// Styles shared with the screen for layout
const screenStyles = StyleSheet.create({
  container: { flex: 1, backgroundColor: AdminColors.bgPage },
  content: { padding: 32, paddingBottom: 60 },
  pageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
    flexWrap: 'wrap',
    gap: 12,
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.bold,
    lineHeight: 40,
  },
  headerActions: {
    flexDirection: 'row',
    gap: 12,
  },
});

const styles = StyleSheet.create({
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
  },
  section: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    padding: 20,
    minWidth: 280,
    flex: 1,
    shadowColor: '#005EC0',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.08,
    shadowRadius: 24,
    elevation: 2,
  },
  fullWidth: {
    width: '100%',
    flex: undefined,
  },
  employeeSection: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    padding: 20,
    marginTop: 20,
    width: '100%',
    shadowColor: '#005EC0',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.08,
    shadowRadius: 24,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.bold,
    marginBottom: 12,
    paddingBottom: 6,
    borderBottomWidth: 2,
    borderBottomColor: AdminColors.borderTable,
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 2,
    paddingBottom: 6,
  },
  fieldGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 0,
  },
  field: {
    width: '50%',
    paddingVertical: 6,
    paddingRight: 12,
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: AdminColors.gray450,
    textTransform: 'uppercase',
    fontFamily: FontFamily.bold,
    letterSpacing: 0.3,
    marginBottom: 3,
  },
  fieldValue: {
    fontSize: 13,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    lineHeight: 18,
  },
  empRowAlternate: {
    backgroundColor: '#F5F9FF',
  },
  summaryText: {
    fontSize: 13,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
    marginBottom: 4,
  },
  emptyMsg: {
    fontSize: 13,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
    paddingVertical: 16,
    textAlign: 'center',
  },
});

// ─── Route Management specific styles ─────────────────────────────────────────
const routeStyles = StyleSheet.create({
  sectionHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
    paddingBottom: 8,
    borderBottomWidth: 2,
    borderBottomColor: AdminColors.borderTable,
  },
  sectionHeading: {
    fontSize: 16,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.bold,
  },
  routeCount: {
    fontSize: 12,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
  },
  duoSection: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.borderDefault,
    marginBottom: 12,
  },
  duoToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  duoLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.medium,
  },
  duoToggle: {
    width: 40,
    height: 22,
    borderRadius: 11,
    backgroundColor: AdminColors.borderDefault,
    justifyContent: 'center',
    padding: 2,
  },
  duoToggleActive: {
    backgroundColor: AdminColors.green400,
  },
  duoThumb: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#fff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  duoThumbActive: {
    alignSelf: 'flex-end',
  },
  duoStatusText: {
    fontSize: 13,
    fontWeight: '500',
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.medium,
  },
  searchAndButtonsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
    gap: 12,
    flexWrap: 'wrap',
  },
  searchLeftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    minWidth: 250,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 32,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    backgroundColor: AdminColors.bgSurface,
    flex: 1,
    minWidth: 180,
  },
  searchIcon: {
    fontSize: 14,
    marginRight: 6,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    outlineWidth: 0,
  } as any,
  routeCountLabel: {
    fontSize: 12,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
  },
  buttonGroup: {
    flexDirection: 'row',
    gap: 8,
  },
  btnSecondary: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: AdminColors.blue800,
    backgroundColor: AdminColors.bgSurface,
    justifyContent: 'center',
    alignItems: 'center',
  },
  btnSecondaryText: {
    fontSize: 13,
    fontWeight: '700',
    color: AdminColors.blue800,
    fontFamily: FontFamily.bold,
  },
  btnPrimary: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 24,
    backgroundColor: AdminColors.blue800,
    justifyContent: 'center',
    alignItems: 'center',
  },
  btnPrimaryText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#fff',
    fontFamily: FontFamily.bold,
  },
  tableHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderBottomWidth: 2,
    borderBottomColor: AdminColors.borderTable,
  },
  th: {
    fontSize: 11,
    fontWeight: '700',
    color: AdminColors.gray450,
    fontFamily: FontFamily.bold,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.borderTable,
  },
  tableRowAlt: {
    backgroundColor: '#F5F9FF',
  },
  td: {
    fontSize: 13,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
  },
  emptyText: {
    fontSize: 13,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
    paddingVertical: 20,
    textAlign: 'center',
  },
  pagination: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 10,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: AdminColors.borderTable,
  },
  paginationInfo: {
    fontSize: 12,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
  },
  paginationBtns: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  pageBtn: {
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: AdminColors.blue800,
  },
  pageBtnDisabled: {
    borderColor: AdminColors.borderDefault,
  },
  pageBtnText: {
    fontSize: 11,
    fontWeight: '700',
    color: AdminColors.blue800,
    fontFamily: FontFamily.bold,
  },
  pageBtnTextDisabled: {
    color: AdminColors.textMuted,
  },
  pageIndicator: {
    fontSize: 11,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
  },
  // Toggle
  toggleContainer: {
    width: 36,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    padding: 2,
  },
  toggleActive: {
    backgroundColor: AdminColors.green400,
  },
  toggleInactive: {
    backgroundColor: AdminColors.borderDefault,
  },
  toggleThumb: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#fff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  toggleThumbInactive: {
    alignSelf: 'flex-start',
  },
  // Native select fallback
  nativeSelect: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 28,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 4,
    backgroundColor: AdminColors.bgSurface,
  },
  nativeSelectText: {
    flex: 1,
    fontSize: 12,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
  },
  nativeArrow: {
    fontSize: 10,
    color: AdminColors.textSecondary,
    marginLeft: 4,
  },
});

export default LocationDetail;
